import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Box,
  Text,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Wind, Thermometer, Droplets, Gauge, Sunrise, Clock, Calendar } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import { useWidgets, WidgetType, AVAILABLE_WIDGET_TYPES } from "../../hooks/useWidgets";

const MotionBox = motion(Box);

const OptionCard = styled(MotionBox)<{ $theme: AppTheme; $selected?: boolean }>`
  padding: 1rem;
  border-radius: ${THEME.borderRadiusLg};
  border: 2px solid ${({ $theme, $selected }) => 
    $selected ? $theme.primary : $theme.border};
  background: ${({ $theme, $selected }) => 
    $selected ? `${$theme.primary}10` : $theme.backgroundCard};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${({ $theme }) => $theme.primary};
    background: ${({ $theme }) => `${$theme.primary}15`};
    transform: translateY(-2px);
  }
`;

const getIconForType = (type: WidgetType) => {
  switch (type) {
    case "aqi":
    case "pm25":
    case "pm10":
      return Wind;
    case "temperature":
      return Thermometer;
    case "humidity":
      return Droplets;
    case "pressure":
      return Gauge;
    case "sunrise-sunset":
      return Sunrise;
    case "day-length":
      return Clock;
    case "year-progress":
      return Calendar;
    default:
      return Wind;
  }
};

type AddWidgetModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAdd?: (type: WidgetType) => void; // Opcjonalny, hook używa własnego stanu
};

export function AddWidgetModal({ isOpen, onClose, onAdd }: AddWidgetModalProps) {
  const theme = useTheme() as AppTheme;
  const { widgets, addWidget, removeWidget } = useWidgets();

  const handleToggle = (type: WidgetType) => {
    const existingWidget = widgets.find(w => w.type === type);
    if (existingWidget) {
      removeWidget(existingWidget.id);
      // Wywołaj callback jeśli jest przekazany (dla kompatybilności wstecznej)
      if (onAdd) {
        // onAdd jest używany tylko do dodawania, więc nie wywołujemy go przy usuwaniu
      }
    } else {
      addWidget(type);
      // Wywołaj callback jeśli jest przekazany (dla kompatybilności wstecznej)
      if (onAdd) {
        onAdd(type);
      }
    }
  };

  const isWidgetAdded = (type: WidgetType) => {
    return widgets.some(w => w.type === type);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
      <ModalOverlay 
        bg="blackAlpha.600"
        backdropFilter="blur(8px)"
      />
      <ModalContent 
        borderRadius={THEME.borderRadiusXl}
        bg={theme.backgroundCard}
        border={`1px solid ${theme.border}`}
        boxShadow={theme.shadowXl}
      >
        <ModalHeader 
          color={theme.text}
          fontFamily="'Outfit', sans-serif"
          fontWeight={700}
          fontSize="xl"
          pb={2}
        >
          Dodaj widżet
        </ModalHeader>
        <ModalCloseButton
          color={theme.textMuted}
          _hover={{ color: theme.text, bg: theme.backgroundMuted }}
          borderRadius="full"
        />
        <ModalBody pt={0} pb={6}>
          <VStack spacing={3} align="stretch">
            {AVAILABLE_WIDGET_TYPES.map((widgetType) => {
              const IconComponent = getIconForType(widgetType.type);
              const isSelected = isWidgetAdded(widgetType.type);
              return (
                <OptionCard
                  key={widgetType.type}
                  $theme={theme}
                  $selected={isSelected}
                  onClick={() => handleToggle(widgetType.type)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Flex align="center" gap={3}>
                    <Box
                      p={2}
                      borderRadius={THEME.borderRadius}
                      bg={isSelected ? `${theme.primary}25` : `${theme.primary}15`}
                      color={theme.primary}
                    >
                      <Icon as={IconComponent} boxSize={5} />
                    </Box>
                    <Text
                      fontFamily="'Outfit', sans-serif"
                      fontSize="md"
                      fontWeight="600"
                      color={theme.text}
                      flex={1}
                    >
                      {widgetType.label}
                    </Text>
                    {isSelected && (
                      <Box
                        width="8px"
                        height="8px"
                        borderRadius="full"
                        bg={theme.primary}
                        boxShadow={`0 0 8px ${theme.primary}60`}
                      />
                    )}
                  </Flex>
                </OptionCard>
              );
            })}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

